import { useLayoutEffect, useRef, type ReactNode } from "react";
import { useLocation } from "react-router";

export type KeepAliveTabPanel = {
  path: string;
  children: ReactNode;
};

const TAB_PATHS = ["/today", "/habits", "/insight", "/rules", "/settings"] as const;

export function isKnownTabPath(pathname: string): pathname is (typeof TAB_PATHS)[number] {
  return (TAB_PATHS as readonly string[]).includes(pathname);
}

type KeepAliveTabPanelsProps = {
  panels: KeepAliveTabPanel[];
};

export function KeepAliveTabPanels({ panels }: KeepAliveTabPanelsProps) {
  const location = useLocation();
  const activePath = isKnownTabPath(location.pathname) ? location.pathname : "/today";

  const scrollByPath = useRef<Partial<Record<string, number>>>({});
  const prevPathRef = useRef(activePath);

  useLayoutEffect(() => {
    const prev = prevPathRef.current;
    if (prev === activePath) return;

    scrollByPath.current[prev] = window.scrollY;
    window.scrollTo(0, scrollByPath.current[activePath] ?? 0);
    prevPathRef.current = activePath;
  }, [activePath]);

  return (
    <>
      {panels.map(({ path, children }) => {
        const isActive = path === activePath;
        return (
          <div
            key={path}
            hidden={!isActive}
            aria-hidden={!isActive}
            inert={!isActive}
            className="screen0-tab-panel"
            data-tab={path}
          >
            {children}
          </div>
        );
      })}
    </>
  );
}
